import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, remove, totals, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const handleCheckout = async () => {
    if (!items.length) return;
    if (!user) {
      toast.info("Please sign in or create an account to check out");
      setOpen(false);
      navigate("/account?next=/shop");
      return;
    }
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map(({ product_id, quantity, size, color, name: n, price, image_url }) => ({
          product_id, quantity, size, color, name: n, price, image_url,
        })),
        origin_url: window.location.origin,
        customer_email: email || undefined,
        customer_name: name || undefined,
      });
      // Save session so /payment/success knows what to poll
      sessionStorage.setItem("pp_last_session", data.session_id);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Checkout failed");
      setCheckingOut(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md bg-white flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="border-b border-silver-light pb-4">
          <SheetTitle className="font-serif text-2xl text-navy-dark flex items-center gap-2">
            <ShoppingBag size={20} className="text-navy-royal" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          {items.length === 0 ? (
            <div className="text-center py-12 text-navy/60" data-testid="cart-empty">
              <ShoppingBag size={40} className="mx-auto mb-4 text-silver-dark" />
              <p className="font-medium">Your cart is empty</p>
              <p className="text-sm mt-1">Discover our handcrafted collection.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.key || item.product_id} className="flex gap-3" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.image_url} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div className="font-medium text-navy-dark text-sm leading-tight">{item.name}</div>
                      <button onClick={() => remove(item.key || item.product_id)} className="text-navy/40 hover:text-cpr" aria-label="Remove" data-testid={`remove-${item.product_id}`}>
                        <X size={16} />
                      </button>
                    </div>
                    {(item.size || item.color) && <div className="text-xs text-navy/50 mt-0.5">{[item.size, item.color].filter(Boolean).join(" · ")}</div>}
                    <div className="text-navy-royal font-semibold mt-1">${item.price.toFixed(2)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(item.key || item.product_id, item.quantity - 1)} className="w-7 h-7 rounded border border-silver-light hover:border-navy flex items-center justify-center" data-testid={`qty-dec-${item.product_id}`}>
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <button onClick={() => updateQty(item.key || item.product_id, item.quantity + 1)} className="w-7 h-7 rounded border border-silver-light hover:border-navy flex items-center justify-center" data-testid={`qty-inc-${item.product_id}`}>
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-silver-light pt-4 space-y-3">
            <div className="flex justify-between text-sm text-navy/70">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">${totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="text-xs text-navy/50">Shipping & taxes calculated at checkout.</div>

            {user ? (
              <div className="text-xs text-navy/60 bg-slate-50 rounded-lg px-3 py-2" data-testid="cart-signed-in">
                Signed in as <span className="font-semibold text-navy-dark">{user.email}</span> — your confirmation will be sent here.
              </div>
            ) : (
              <>
                <Input placeholder="Your email (for confirmation)" value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="cart-email-input" />
                <Input placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="cart-name-input" />
              </>
            )}

            <Button
              onClick={handleCheckout}
              disabled={checkingOut}
              className="w-full bg-navy-dark hover:bg-navy-royal text-white font-bold uppercase tracking-[0.15em] text-xs rounded-md py-6"
              data-testid="checkout-btn"
            >
              {checkingOut ? "Redirecting…" : `Checkout — $${totals.subtotal.toFixed(2)}`}
            </Button>
            <button onClick={clear} className="w-full text-xs text-navy/50 hover:text-navy" data-testid="cart-clear">Clear cart</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
